import { useState, useEffect, useCallback, useRef } from 'react';

interface BatteryStatus {
  level: number;       // 0-100
  charging: boolean;
  supported: boolean;
}

interface BatteryManager extends EventTarget {
  charging: boolean;
  chargingTime: number;
  dischargingTime: number;
  level: number;
  onchargingchange: (() => void) | null;
  onlevelchange: (() => void) | null;
}

/**
 * Monitors battery level and charging state.
 * - Shows warning when battery drops below LOW_THRESHOLD (20%)
 * - Shows warning when charging above HIGH_THRESHOLD (80%)
 * - Fires onLowBattery / onHighBattery callbacks for MQTT alerts & TTS
 * - Uses the Web Battery API (supported in Chromium-based WebViews)
 */
const LOW_THRESHOLD = 20;
const HIGH_THRESHOLD = 65;  // TODO: restore to 80 after testing

export function useBattery() {
  const [battery, setBattery] = useState<BatteryStatus>({
    level: 100,
    charging: false,
    supported: false,
  });
  const [showLowWarning, setShowLowWarning] = useState(false);
  const [showHighWarning, setShowHighWarning] = useState(false);
  const lowDismissedRef = useRef(false);
  const highDismissedRef = useRef(false);

  // Track whether we already fired the threshold event (reset when level crosses back)
  const lowFiredRef = useRef(false);
  const highFiredRef = useRef(false);

  // Callbacks set by BatteryWarning for MQTT + TTS
  const onLowBatteryRef = useRef<((level: number) => void) | null>(null);
  const onHighBatteryRef = useRef<((level: number) => void) | null>(null);

  useEffect(() => {
    let batteryManager: BatteryManager | null = null;

    function update() {
      if (!batteryManager) return;
      const level = Math.round(batteryManager.level * 100);
      const charging = batteryManager.charging;
      setBattery({ level, charging, supported: true });

      // Low battery warning (re-triggers if it drops below threshold again after going above)
      if (level <= LOW_THRESHOLD && !charging) {
        if (!lowDismissedRef.current) setShowLowWarning(true);
        // Fire callback once per threshold crossing
        if (!lowFiredRef.current) {
          lowFiredRef.current = true;
          onLowBatteryRef.current?.(level);
        }
      } else {
        lowDismissedRef.current = false;
        lowFiredRef.current = false;
        setShowLowWarning(false);
      }

      // Overcharge warning
      if (level >= HIGH_THRESHOLD && charging) {
        if (!highDismissedRef.current) setShowHighWarning(true);
        if (!highFiredRef.current) {
          highFiredRef.current = true;
          onHighBatteryRef.current?.(level);
        }
      } else {
        highDismissedRef.current = false;
        highFiredRef.current = false;
        setShowHighWarning(false);
      }
    }

    async function init() {
      try {
        const nav = navigator as any;
        if (!nav.getBattery) return;
        batteryManager = await nav.getBattery();
        if (!batteryManager) return;

        batteryManager.addEventListener('chargingchange', update);
        batteryManager.addEventListener('levelchange', update);
        update();
      } catch {
        // Battery API not supported
      }
    }

    init();

    return () => {
      if (batteryManager) {
        batteryManager.removeEventListener('chargingchange', update);
        batteryManager.removeEventListener('levelchange', update);
      }
    };
  }, []);

  const dismissLow = useCallback(() => {
    lowDismissedRef.current = true;
    setShowLowWarning(false);
  }, []);

  const dismissHigh = useCallback(() => {
    highDismissedRef.current = true;
    setShowHighWarning(false);
  }, []);

  const setOnLowBattery = useCallback((cb: (level: number) => void) => {
    onLowBatteryRef.current = cb;
  }, []);

  const setOnHighBattery = useCallback((cb: (level: number) => void) => {
    onHighBatteryRef.current = cb;
  }, []);

  return {
    battery,
    showLowWarning,
    showHighWarning,
    dismissLow,
    dismissHigh,
    setOnLowBattery,
    setOnHighBattery,
    LOW_THRESHOLD,
    HIGH_THRESHOLD,
  };
}
